/**
 * TransitionEditor Component
 *
 * Modal dialog for viewing transition (edge) details and deleting connections.
 *
 * Features:
 * - Display source/target screen names (read-only)
 * - Delete transition with confirmation
 * - Edit conditions button (opens ConditionEditorDialog)
 *
 * Related: Sprint-5, FR-041 to FR-045
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Button, useTheme, TextField, MenuItem } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import type { Edge } from '@xyflow/react';
import type { ScreenTransition } from '@segmently/schema-models/v2';
import {
  THEME_EASING_OPTIONS,
  THEME_TRANSITION_TYPE_OPTIONS,
} from '@modules/themes/animation/themeAnimationPresets';
import type { TransitionRule } from '@modules/reactFlow/types/EntityTransition';
import { EdgeDeleteConfirmation } from './EdgeDeleteConfirmation';

const EDGE_TRANSITION_DURATION_MIN = 50;
const EDGE_TRANSITION_DURATION_MAX = 10000;
const EDGE_TRANSITION_DEFAULT_DURATION = 300;
const EDGE_TRANSITION_DEFAULT_EASING = 'easeInOutCubic';

function clampDuration(value: number): number {
  if (Number.isNaN(value)) return EDGE_TRANSITION_DURATION_MIN;
  return Math.min(EDGE_TRANSITION_DURATION_MAX, Math.max(EDGE_TRANSITION_DURATION_MIN, value));
}

export interface TransitionEditorProps {
  /** Whether dialog is open */
  open: boolean;

  /** Callback when dialog is closed */
  onClose: () => void;

  /** Edge data to display and edit */
  edgeData: Edge | null;

  /** Callback when edge is deleted */
  onEdgeDelete?: (edgeId: string) => void;

  /** Callback when "Edit Conditions" is clicked */
  onEditConditions?: (edgeId: string, sourceScreenId: string, targetScreenId: string, currentRules: TransitionRule[]) => void;

  /**
   * Callback persisting the per-edge visual transition override
   * (Route.transition). null = Inherit — DELETE the override. The group
   * renders only when this callback is provided (BlockCanvasEditor usage
   * stays untouched).
   */
  onTransitionSave?: (edgeId: string, sourceScreenId: string, transition: ScreenTransition | null) => void;
}

export const TransitionEditor: React.FC<TransitionEditorProps> = ({
  open,
  onClose,
  edgeData,
  onEdgeDelete,
  onEditConditions,
  onTransitionSave,
}) => {
  const theme = useTheme();

  // State for delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Per-edge transition override — local mirror of edge data, saved on change
  // (type/easing) or on blur (duration, to avoid a write per keystroke)
  const [transitionOverride, setTransitionOverride] = useState<ScreenTransition | null>(null);
  useEffect(() => {
    if (!open) return;
    setTransitionOverride(
      ((edgeData?.data as { transition?: ScreenTransition } | undefined)?.transition) ?? null,
    );
  }, [open, edgeData]);

  const saveTransition = useCallback(
    (next: ScreenTransition | null) => {
      setTransitionOverride(next);
      if (edgeData?.id && edgeData?.source) {
        onTransitionSave?.(edgeData.id, edgeData.source, next);
      }
    },
    [edgeData, onTransitionSave],
  );

  const handleTransitionTypeChange = useCallback(
    (value: string) => {
      if (!value) {
        saveTransition(null); // Inherit — delete the override
        return;
      }
      saveTransition({
        type: value,
        timing: {
          type: 'tween',
          duration: transitionOverride?.timing?.duration ?? EDGE_TRANSITION_DEFAULT_DURATION,
          easing: transitionOverride?.timing?.easing ?? EDGE_TRANSITION_DEFAULT_EASING,
        },
      } as ScreenTransition);
    },
    [saveTransition, transitionOverride],
  );

  // Handle delete button click
  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  // Handle delete confirmation
  const confirmDelete = useCallback(() => {
    if (edgeData?.id) {
      onEdgeDelete?.(edgeData.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  }, [edgeData, onEdgeDelete, onClose]);

  // Handle delete cancellation
  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  // Handle edit conditions button click
  const handleEditConditions = useCallback(() => {
    if (edgeData?.id && edgeData?.source && edgeData?.target) {
      // Extract current rules from edge data if available
      const currentRules: TransitionRule[] = (edgeData.data as any)?.rules || [];
      onEditConditions?.(edgeData.id, edgeData.source, edgeData.target, currentRules);
    }
  }, [edgeData, onEditConditions]);

  // Check if conditions editing is available
  const canEditConditions = Boolean(onEditConditions && edgeData);

  return (
    <>
      {/* Main Transition Editor Dialog */}
      <BaseDialog
        open={open}
        onClose={onClose}
        mode="modal"
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader
          title="Connection Details"
          onClose={onClose}
        />

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
            {/* Source Screen (Read-Only) */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                From Screen:
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: theme.typography.fontWeightMedium }}
              >
                {(edgeData?.data as any)?.sourceScreen?.name || edgeData?.source || 'N/A'}
              </Typography>
              {(edgeData?.data as any)?.sourceScreen?.name && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, display: 'block' }}
                >
                  ID: {edgeData?.source}
                </Typography>
              )}
            </Box>

            {/* Target Screen (Read-Only) */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                To Screen:
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: theme.typography.fontWeightMedium }}
              >
                {(edgeData?.data as any)?.targetScreen?.name || edgeData?.target || 'N/A'}
              </Typography>
              {(edgeData?.data as any)?.targetScreen?.name && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, display: 'block' }}
                >
                  ID: {edgeData?.target}
                </Typography>
              )}
            </Box>

            {/* Per-edge visual transition override (Route.transition) */}
            {Boolean(onTransitionSave && edgeData) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="overline" sx={{ color: theme.palette.text.secondary }}>
                  Transition
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Type"
                  value={transitionOverride?.type ?? ''}
                  onChange={(e) => handleTransitionTypeChange(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'edge-transition-type' } }}
                >
                  <MenuItem value="">Inherit (funnel default)</MenuItem>
                  {THEME_TRANSITION_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Duration (ms)"
                    sx={{ flex: 1 }}
                    value={transitionOverride?.timing?.duration ?? EDGE_TRANSITION_DEFAULT_DURATION}
                    disabled={!transitionOverride}
                    onChange={(e) => {
                      // Local-only while typing; persisted on blur
                      if (!transitionOverride) return;
                      setTransitionOverride({
                        ...transitionOverride,
                        timing: { ...transitionOverride.timing, duration: Number(e.target.value) },
                      } as ScreenTransition);
                    }}
                    onBlur={() => {
                      if (!transitionOverride) return;
                      saveTransition({
                        ...transitionOverride,
                        timing: {
                          ...transitionOverride.timing,
                          duration: clampDuration(Number(transitionOverride.timing?.duration)),
                        },
                      } as ScreenTransition);
                    }}
                    slotProps={{
                      htmlInput: {
                        min: EDGE_TRANSITION_DURATION_MIN,
                        max: EDGE_TRANSITION_DURATION_MAX,
                        'data-testid': 'edge-transition-duration',
                      },
                    }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Easing"
                    sx={{ flex: 1 }}
                    value={transitionOverride?.timing?.easing ?? EDGE_TRANSITION_DEFAULT_EASING}
                    disabled={!transitionOverride}
                    onChange={(e) => {
                      if (!transitionOverride) return;
                      saveTransition({
                        ...transitionOverride,
                        timing: { ...transitionOverride.timing, easing: e.target.value },
                      } as ScreenTransition);
                    }}
                    slotProps={{ htmlInput: { 'data-testid': 'edge-transition-easing' } }}
                  >
                    {THEME_EASING_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {transitionOverride
                    ? 'Overrides the funnel default for this connection only. Set Type to Inherit to remove.'
                    : "This connection plays the funnel's default transition."}
                </Typography>
              </Box>
            )}

            {/* Edit Conditions Button */}
            {canEditConditions && (
              <Box sx={{ marginTop: theme.spacing(2) }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={handleEditConditions}
                  fullWidth
                >
                  Edit Conditions
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogFooter
          primaryAction={{
            label: 'Delete Connection',
            onClick: handleDelete,
            variant: 'outlined',
            color: 'error',
          }}
        />
      </BaseDialog>

      {/* Delete Confirmation Dialog */}
      <EdgeDeleteConfirmation
        open={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
      />
    </>
  );
};
